import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import React from 'react'

import ActionsBox from '@/app/module/campaigns/component/actions/actions-box'
import { useCampaignActionContext } from '@/app/module/campaigns/component/item-provider'
import { SMS_ACTION_DEFAULT_SENDERID } from '@/app/module/campaigns/definitions'
import {
  ActionMessage,
  ActionsOnly,
  CampaignTemplateType,
  Language,
  LocalAction,
  PartFamily,
  SenderIdOption,
} from '@/app/module/campaigns/types'
import { getActionOptionsForCampaign } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'
import { unwrapActions, wrapActions } from '@/app/module/campaigns/utils/actions/wrap'
import { CountryTimezone, FilesType, PersonalizationType, TimezoneWithCountryCodeType } from '@/app/types'

import { GetImageProps, GetVoiceProps } from './part-config-props'

type Props = {
  item: ActionMessage
  id: string
  personalizationList: PersonalizationType
  senderIds: SenderIdOption[]
  title: string
  whatsappSenderIds: SenderIdOption[]
  orgId: number
  token: string
  files: FilesType
  timezone: string
  timezones: TimezoneWithCountryCodeType
  countryCode: string
  countriesTimezones: CountryTimezone[]
  languages: Language[]
  defaultLanguage: string
  type: CampaignTemplateType
  index: number

  changeHandler: (item: Partial<ActionMessage>, changed?: boolean) => void
  getImageProps: GetImageProps
  getVoiceProps: GetVoiceProps
}

export default function CampaignContentQuestionAction(props: Props) {
  const { hasQuotas } = useCampaignActionContext()
  const {
    item,
    id,
    personalizationList,
    title,
    senderIds,
    whatsappSenderIds,
    orgId,
    token,
    timezone,
    timezones,
    countryCode,
    countriesTimezones,
    languages,
    defaultLanguage,
    files,
    getImageProps,
    getVoiceProps,
  } = props

  const [state, setState] = React.useState(fromProps(props.item))

  React.useEffect(() => {
    setState(fromProps(item))
  }, [item])

  const changeHandler = (newItem: { actionsOnly: LocalAction[] }) => {
    setState({
      ...state,
      ...newItem,
    })
    const returnToProps = backToProps({
      ...state,
      ...newItem,
    })

    props.changeHandler({
      ...item,
      ...returnToProps,
    })
  }

  const actionValues = React.useMemo(
    () =>
      getActionOptionsForCampaign({
        campaignType: props.type,
        partType: PartFamily.Action,
        hasQuotas,
        actionNamespace: ActionContext.ActionOnly,
      }),
    [hasQuotas, props.type],
  )

  return (
    <div id="action-block-cont" data-testid="actions-only-container">
      <Divider
        style={{
          margin: '0rem -24px 1rem',
        }}
      />
      <div>
        <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
          {title}
        </Typography>
        <div>
          <ActionsBox
            actionOptions={actionValues}
            actions={state.actionsOnly}
            countriesTimezones={countriesTimezones}
            countryCode={countryCode}
            defaultLanguage={defaultLanguage}
            defaultSenderId={SMS_ACTION_DEFAULT_SENDERID.actionsOnly}
            disableLabel
            files={files}
            hasTranscribe={false}
            index={props.index}
            isCloseDisabled
            languages={languages}
            orgId={orgId}
            personalizationList={personalizationList}
            senderIds={senderIds}
            timezone={timezone}
            timezones={timezones}
            token={token}
            transcribeEnabled={false}
            updateContactConfig={{
              invalid: false,
              openResponse: false,
              question: false,
            }}
            voiceId={`${id}-action`}
            whatsappSenderIds={whatsappSenderIds}
            onChange={(actionsList: LocalAction[]) =>
              changeHandler({
                actionsOnly: actionsList,
              })
            }
            getImageProps={getImageProps}
            getVoiceProps={getVoiceProps}
          />
        </div>
      </div>
    </div>
  )
}

const fromProps = ({ actionsOnly }: { actionsOnly: ActionsOnly }) => ({
  actionsOnly: unwrapActions(actionsOnly?.actions || []),
})

const backToProps = ({ actionsOnly }: { actionsOnly: LocalAction[] }) => ({
  actionsOnly: {
    actions: wrapActions(actionsOnly),
  },
})

export type { Props as CampaignContentQuestionActionProps }
